import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import { IApiResponseError, IApiState } from '../../../types/api';
import { RootState } from '../..';
import { showAlert } from '../appToast/appToastSlice';
import { getTransactionsPendingApprovalAsync } from '../transactionMandateV2/getPendingTransactionsSlice';

const BULK_APPROVE_PATH = 'transaction/bulk/approve';

interface IBulkApproveTransactionRequest {
  approvalIds: string[];
  pin: string;
}

interface IBulkApproveTransactionResponse {
  status?: boolean;
  message?: string;
  data?: unknown;
  errors?: IApiResponseError[];
}

const initialState: IApiState<IBulkApproveTransactionResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const bulkApproveTransactionAsync = createAsyncThunk(
  BULK_APPROVE_PATH,
  async (
    values: IBulkApproveTransactionRequest,
    { rejectWithValue, dispatch, getState },
  ) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;

      const businessId =
        localStorage.getItem('businessId') ||
        state.auth.value?.data?.user?.businesses?.[0]?.id;

      const config = {
        headers: {
          Authorization: `Token ${accessToken}`,
          'X-Business-Id': businessId,
        },
      };

      const { data } = await api.put<IBulkApproveTransactionResponse>(
        BULK_APPROVE_PATH,
        {
          pin: values.pin,
          approvalIds: values.approvalIds,
        },
        config,
      );

      dispatch(getTransactionsPendingApprovalAsync({}));

      dispatch(
        showAlert({
          status: 'success',
          description: data?.message ?? 'Transactions successfully approved',
        }),
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IBulkApproveTransactionResponse>;

      return rejectWithValue(error.response?.data);
    }
  },
);

export const bulkTransactionMandateCreationSlice = createSlice({
  name: BULK_APPROVE_PATH,
  initialState,
  reducers: {
    resetTransactionBulkApprove(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(bulkApproveTransactionAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(bulkApproveTransactionAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IBulkApproveTransactionResponse;
      })
      .addCase(bulkApproveTransactionAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IBulkApproveTransactionResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetTransactionBulkApprove } =
  bulkTransactionMandateCreationSlice.actions;

export default bulkTransactionMandateCreationSlice.reducer;
